/**
 * Analytics Service for WeatherTeller Ecosystem
 * 
 * This service handles unified analytics tracking across all platforms
 * including cross-domain tracking between website, forum, blog, and app
 * with consistent event naming and user identification.
 */

// Google Analytics Tracking ID
export const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || 'G-HX5590YKW4';

// Google Tag Manager ID
export const GTM_ID = process.env.REACT_APP_GTM_ID || 'GT-K8HQWBGN';

// Google Analytics Stream ID
export const GA_STREAM_ID = process.env.REACT_APP_GA_STREAM_ID || '10385667123';

// Ecosystem part identifier
export const ECOSYSTEM_PARTS = {
  WEBSITE: 'website',
  FORUM: 'forum',
  BLOG: 'blog',
  APP: 'app'
};

/**
 * Initialize Google Tag Manager with ecosystem part identification
 * @param {string} ecosystemPart - Which part of the ecosystem (website, forum, blog, app)
 */
export const initializeGTM = (ecosystemPart = ECOSYSTEM_PARTS.WEBSITE) => {
  if (typeof window === 'undefined') return;
  
  // Initialize dataLayer with ecosystem part
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
    ecosystem_part: ecosystemPart
  });
  
  // Create script element for GTM
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  
  // Insert the script in the head
  const head = document.getElementsByTagName('head')[0];
  head.insertBefore(script, head.firstChild);
  
  // Add noscript fallback
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
  
  console.log(`Google Tag Manager initialized for ${ecosystemPart} with ID: ${GTM_ID}`);
};

/**
 * Initialize Google Analytics with cross-domain tracking
 * @param {string} ecosystemPart - Which part of the ecosystem (website, forum, blog, app)
 */
export const initializeGA = (ecosystemPart = ECOSYSTEM_PARTS.WEBSITE) => {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', GA_TRACKING_ID, {
    page_path: window.location.pathname + window.location.search,
    cookie_flags: 'SameSite=None;Secure',
    stream_id: GA_STREAM_ID,
    // Cross-domain tracking configuration
    linker: {
      domains: [
        'weatherteller.com',
        'app.weatherteller.com', 
        'forum.weatherteller.com',
        'blog.weatherteller.com'
      ]
    },
    // Custom dimension for ecosystem part
    custom_map: {
      dimension1: 'ecosystem_part'
    },
    ecosystem_part: ecosystemPart
  });
  
  // Create script element for GA
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  
  // Insert the script in the head
  const head = document.getElementsByTagName('head')[0];
  head.insertBefore(script, head.firstChild);
  
  console.log(`Google Analytics initialized for ${ecosystemPart} with ID: ${GA_TRACKING_ID}`);
};

/**
 * Master initialization function for all analytics services
 * @param {string} ecosystemPart - Which part of the ecosystem (website, forum, blog, app)
 */
export const initializeAnalytics = (ecosystemPart = ECOSYSTEM_PARTS.WEBSITE) => {
  initializeGTM(ecosystemPart);
  initializeGA(ecosystemPart);
  
  console.log(`All analytics services initialized for: ${ecosystemPart}`);
  return {
    trackPageView: (url) => pageview(url),
    trackEvent: (action, category, label, value) => event({ action, category, label, value }),
    identifyUser: (user) => identifyUser(user),
    trackForumEvents,
    trackWebsiteEvents,
    trackBlogEvents,
    trackAppEvents
  };
};

/**
 * Track a pageview in Google Analytics
 * @param {string} url - The URL to track (defaults to current path)
 */
export const pageview = (url = window.location.pathname + window.location.search) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('config', GA_TRACKING_ID, {
    page_path: url,
    stream_id: GA_STREAM_ID
  });
  
  // Also push to dataLayer for GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'pageview',
      page_path: url
    });
  }
};

/**
 * Track an event in Google Analytics
 * @param {object} params - Event parameters
 */
export const event = ({ action, category, label, value, ...customParams }) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...customParams
  });
  
  // Also push to dataLayer for GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'custom_event',
      event_action: action,
      event_category: category,
      event_label: label,
      event_value: value,
      ...customParams
    });
  }
};

/**
 * Identify user in analytics systems
 * @param {object} user - User object containing id, email, etc.
 */
export const identifyUser = (user) => {
  if (!user || typeof window === 'undefined') return;
  
  // Set user ID for GA4
  if (window.gtag) {
    window.gtag('set', {
      'user_id': user.uid || user.id
    });
  }
  
  // Push to dataLayer for GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'user_identified',
      user_id: user.uid || user.id,
      user_email: user.email,
      is_admin: user.isAdmin || false,
      login_method: user.providerData?.[0]?.providerId || 'unknown'
    });
  }
  
  console.log(`User identified in analytics: ${user.uid || user.id}`);
};

/**
 * Website-specific tracking events
 */
export const trackWebsiteEvents = {
  featureClick: (featureName) => event({ 
    action: 'feature_click', 
    category: 'website', 
    label: featureName 
  }),
  demoRequest: (demoType) => event({ 
    action: 'demo_request', 
    category: 'website', 
    label: demoType 
  }),
  newsletterSignup: (source) => event({ 
    action: 'newsletter_signup', 
    category: 'website', 
    label: source 
  })
};

/**
 * Forum-specific tracking events
 */
export const trackForumEvents = {
  postCreated: (categoryId, postType) => event({ 
    action: 'post_created', 
    category: 'forum', 
    label: postType,
    categoryId: categoryId 
  }),
  postViewed: (postId) => event({ 
    action: 'post_viewed', 
    category: 'forum', 
    label: postId 
  }),
  categorySelected: (categoryId) => event({ 
    action: 'category_selected', 
    category: 'forum', 
    label: categoryId 
  }),
  donationStarted: (source) => event({ 
    action: 'donation_started', 
    category: 'forum', 
    label: source 
  }),
  donationCompleted: (amount, currency) => event({ 
    action: 'donation_completed', 
    category: 'forum', 
    value: amount,
    currency: currency 
  }),
  adminAction: (actionType) => event({ 
    action: 'admin_action', 
    category: 'forum', 
    label: actionType 
  })
};

/**
 * Blog-specific tracking events
 */
export const trackBlogEvents = {
  articleViewed: (articleId, title) => event({ 
    action: 'article_viewed', 
    category: 'blog', 
    label: title,
    articleId: articleId 
  }),
  categoryViewed: (category) => event({ 
    action: 'category_viewed', 
    category: 'blog', 
    label: category 
  }),
  commentAdded: (articleId) => event({ 
    action: 'comment_added', 
    category: 'blog', 
    label: articleId 
  }),
  shareClicked: (platform, articleId) => event({ 
    action: 'share_clicked', 
    category: 'blog', 
    label: platform,
    articleId: articleId 
  })
};

/**
 * App-specific tracking events
 */
export const trackAppEvents = {
  featureUsed: (featureName) => event({ 
    action: 'feature_used', 
    category: 'app', 
    label: featureName 
  }),
  weatherReportSubmitted: (reportType) => event({ 
    action: 'weather_report_submitted', 
    category: 'app', 
    label: reportType 
  }),
  locationUpdated: (source) => event({ 
    action: 'location_updated', 
    category: 'app', 
    label: source 
  }),
  searchPerformed: (query) => event({ 
    action: 'search_performed', 
    category: 'app', 
    label: query 
  })
};

export default {
  initializeGTM,
  initializeGA,
  initializeAnalytics,
  pageview,
  event,
  identifyUser,
  trackWebsiteEvents,
  trackForumEvents,
  trackBlogEvents,
  trackAppEvents,
  GA_TRACKING_ID,
  GTM_ID,
  GA_STREAM_ID,
  ECOSYSTEM_PARTS
};